import { isString } from 'lodash';
import { useEffect, useMemo, useRef } from 'react';

import { ValidationRules } from '@dreryk/react-common';
import { FormLabelPosition, Textarea } from '@dreryk/react-components';

import { getDosageMaxLength, getDosageMinLength, getDosageRegExp } from '~libs.shared.prescription';

import { prescriptionTranslationKeys } from '../../../../../translations';
import {
   DraftPrescriptionFieldName,
   DraftPrescriptionsFormBean,
   DraftPrescriptionsFormController,
} from '../../draftPrescriptions/DraftPrescriptionsForm.types';

import { Dosage } from './dosage.types';

const DOSAGE_VALIDATION_RULES: ValidationRules<DraftPrescriptionsFormBean> = {
   minLength: {
      value: getDosageMinLength(),
      message: prescriptionTranslationKeys.dosage.DS3Chars,
   },
   validate: (data) => {
      if (isString(data) && !getDosageRegExp().test(data.trim())) {
         return prescriptionTranslationKeys.dosage.DS3Chars;
      }

      return undefined;
   },
};

type DosageInputProps = Pick<DraftPrescriptionsFormController, 'register' | 'trigger'> & {
   readonly name: DraftPrescriptionFieldName;
   readonly defaultValue: Dosage | null;
   readonly required: boolean | undefined;
};

export const DosageInput = (props: DosageInputProps): JSX.Element => {
   const elementRef = useRef<HTMLTextAreaElement | null>(null);

   useEffect(() => {
      const element = elementRef.current;
      const triggerValidation = props.trigger;
      const fieldName = props.name;
      const handleBlur = (): void => {
         triggerValidation(fieldName);
      };

      if (element) {
         element.autocomplete = 'off';
      }

      element?.addEventListener('blur', handleBlur);

      return () => element?.removeEventListener('blur', handleBlur);
   }, [props.trigger, props.name]);

   const validationRules = useMemo(
      () => ({
         ...DOSAGE_VALIDATION_RULES,
         required: props.required
            ? {
                 value: true,
                 message: prescriptionTranslationKeys.dosage.DS3Chars,
              }
            : false,
      }),
      [props.required]
   );
   return (
      <Textarea
         controller={props.register(props.name, validationRules)}
         elementRef={elementRef}
         defaultValue={props.defaultValue}
         label={prescriptionTranslationKeys.dosage.DS}
         labelPosition={FormLabelPosition.LEFT}
         placeholder={
            props.required
               ? prescriptionTranslationKeys.dosage.DSPlaceholder
               : prescriptionTranslationKeys.dosage.informationForPatientPlaceholder
         }
         withoutRequiredMark
         focus={false}
         rows={1}
         maxRows={getDosageMaxLength()}
         maxLength={getDosageMaxLength()}
         compact
         testId={'dosage'}
      />
   );
};
